import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { useMemo } from 'react'
import { ArrowRight, Sparkles } from 'lucide-react'
import { DEFAULT_MODEL_CONFIG } from '@modelstack/shared-schema/auth'
import { familyToProvider } from '../../../shared-pricing-config'
import { getAllowedDurations } from '../../../shared-generation-pricing-config'
import type { ProviderTier } from '../../../shared-pricing-config'
import { modelCatalog } from '@/features/models/services/modelCatalogService'
import { getModelUrlType, isChatModel, isGenerationModel } from '@/features/models/types/unified-model.types'
import { parseGenerationId } from '@/features/models/utils/generationModel'
import { GENERATION_FAMILY_LABELS } from '@/shared/utils/generationPricing'
import {
  ModelDetailBreadcrumb,
  ModelDetailCapabilities,
  ModelDetailCodeExamples,
  ModelDetailFAQ,
  ModelDetailGenerationCodeExamples,
  ModelDetailGenerationFAQ,
  ModelDetailGenerationParams,
  ModelDetailGenerationPricing,
  ModelDetailGenerationRelated,
  ModelDetailGenerationSpecs,
  ModelDetailHeader,
  ModelDetailPricing,
  ModelDetailRelated,
  ModelDetailStats,
} from '@/features/models/components'
import { useUserTierPreferences } from '@/features/user-settings/hooks/useUserTierPreferences'
import { useModelCatalog, useModelUptimeMap } from '@/shared/hooks/useModelCatalog'
import { ModelDetailSidebar } from '@/features/models/components/ModelDetailSidebar'
import { CatalogBackdrop } from '@/features/models/components/CatalogBackdrop'
import { useSession } from '@/server/infrastructure/auth/client'
import { AppNotFoundPage } from '@/features/shared/components'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { Button } from '@/components/ui/button'
import { generateLinks, generateMetaTags, notFoundSEO } from '@/lib/seo'
import { BASE_URL } from '@/lib/constants'
import {
  getBreadcrumbSchema,
  getModelOfferSchema,
  renderJsonLd,
} from '@/lib/structured-data'

const VALID_URL_TYPES = ['chat', 'image', 'video', 'audio']

export const Route = createFileRoute('/models/$type/$')({
  loader: ({ params }) => {
    if (!VALID_URL_TYPES.includes(params.type)) throw notFound()

    const modelId = params._splat ?? ''
    const model = modelCatalog.getAllModels().find((m) => m.id === modelId)
    if (!model) throw notFound()
    if (getModelUrlType(model) !== params.type) throw notFound()

    return model
  },
  head: ({ loaderData }) => {
    const model = loaderData
    if (!model) return { meta: generateMetaTags(notFoundSEO) }

    const urlType = getModelUrlType(model)
    const canonical = `/models/${urlType}/${model.id}`
    const title = `${model.name} - Pricing & API | ModelStack`
    const description = model.description || `Access ${model.name} through the ModelStack unified API gateway.`

    return {
      meta: generateMetaTags({ title, description, canonical }),
      links: [
        ...generateLinks({ canonical }),
        {
          rel: 'alternate',
          type: 'text/markdown',
          href: `${BASE_URL}${canonical}.md`,
        },
      ],
    }
  },
  notFoundComponent: AppNotFoundPage,
  component: ModelDetailPage,
})

function ModelDetailPage() {
  const model = Route.useLoaderData()
  const urlType = getModelUrlType(model)
  // Generation models use their own family enum (grok, elevenlabs, replicate,
  // stability, ...), distinct from chat's ModelFamily — familyToProvider()
  // only knows the latter and falls back to "Unknown" for the former.
  const provider = isGenerationModel(model)
    ? (GENERATION_FAMILY_LABELS[model.family] ?? model.family)
    : familyToProvider(model.family)

  const { data: session } = useSession()
  const { data: tierPreferences } = useUserTierPreferences(!!session?.user)
  const enabledTiers: Array<ProviderTier> =
    tierPreferences?.modelConfig.default.tiers ??
    DEFAULT_MODEL_CONFIG.default.tiers

  const catalog = useModelCatalog()
  const uptimeByModel = useModelUptimeMap()
  const availableTiers = catalog.getAvailableTiers(model.id)
  const augmentedModel = {
    ...model,
    availableTiers,
    uptimeHistory: uptimeByModel.get(model.id)?.segments,
  }

  const allModels = useMemo(() => modelCatalog.getAllModels(), [])

  const generation = isGenerationModel(model) ? parseGenerationId(model.id) : null

  const cta = generation ? (
    <Button asChild size="lg">
      <Link to="/dashboard/generations" search={{ model: generation.baseModel, type: generation.genType }}>
        Generate Now <Sparkles className="w-4 h-4" />
      </Link>
    </Button>
  ) : (
    <Button asChild size="lg" className="font-semibold tracking-tight">
      <Link to="/sign-up">
        Get started <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  )

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-clip bg-[var(--color-bg-primary)]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: renderJsonLd(
            getBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Models', url: '/models' },
              { name: model.name, url: `/models/${urlType}/${model.id}` },
            ]),
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: renderJsonLd(
            getModelOfferSchema({
              id: model.id,
              name: model.name,
              description: model.description,
              canonical: `/models/${urlType}/${model.id}`,
              pricing:
                model.pricing.type === 'chat'
                  ? {
                      type: 'chat',
                      inputPricePerMillion: model.pricing.inputPricePerMillion,
                    }
                  : {
                      type: 'generation',
                      priceMillidollars: model.pricing.priceMillidollars,
                      priceUnit: model.pricing.priceUnit,
                    },
            }) as any,
          ),
        }}
      />

      <Header />
      <main className="mx-auto w-full max-w-[1280px] flex-1 px-6 pt-28 pb-20">
        <header className="relative pt-4 pb-12 md:pt-8 md:pb-16">
          <CatalogBackdrop />
          <div className="relative">
            <ModelDetailBreadcrumb provider={provider} modelName={model.name} />
            <ModelDetailHeader model={model} urlType={urlType} provider={provider} />
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]">
              {model.description}
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-12">
          {/* Sidebar first in DOM so it sits above the specs on mobile. */}
          <div className="lg:order-2">
            <ModelDetailSidebar model={augmentedModel} enabledTiers={enabledTiers} cta={cta} />
          </div>

          <div className="min-w-0 lg:order-1">
            {isChatModel(model) ? (
              <>
                <ModelDetailStats meta={model.metadata} />
                <ModelDetailPricing model={augmentedModel} />
                <ModelDetailCapabilities capabilities={model.metadata.capabilities} />
                <ModelDetailCodeExamples modelId={model.id} />
                <ModelDetailFAQ model={model} />
              </>
            ) : isGenerationModel(model) && generation ? (
              <>
                <ModelDetailGenerationSpecs
                  baseModel={generation.baseModel}
                  genType={generation.genType}
                  priceUnit={model.pricing.priceUnit}
                  family={model.family}
                  allowedDurations={getAllowedDurations(generation.baseModel, generation.genType)}
                />
                <ModelDetailGenerationPricing
                  baseModel={generation.baseModel}
                  genType={generation.genType}
                  priceUnit={model.pricing.priceUnit}
                />
                <ModelDetailGenerationParams params={model.metadata.supportedParams ?? []} />
                <ModelDetailGenerationCodeExamples
                  baseModel={generation.baseModel}
                  genType={generation.genType}
                  supportedParams={model.metadata.supportedParams ?? []}
                />
                <ModelDetailGenerationFAQ model={model} baseModel={generation.baseModel} genType={generation.genType} />
              </>
            ) : null}
          </div>
        </div>

        {isChatModel(model) ? (
          <ModelDetailRelated model={model} allModels={allModels} />
        ) : isGenerationModel(model) && generation ? (
          <ModelDetailGenerationRelated model={model} baseModel={generation.baseModel} allModels={allModels} />
        ) : null}
      </main>
      <Footer />
    </div>
  )
}
